
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Eye, X, Trash2 } from "lucide-react";
import { useBrowsingHistory } from "@/hooks/useBrowsingHistory";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import SwipeableRecentProduct from "./SwipeableRecentProduct";

const RecentlyViewedBar = () => {
  const { user } = useOptimizedAuth();
  const userId = user?.id || 'anonymous';
  const { browsingHistory, isLoading, removeFromHistory, clearHistory, hasHistory } = useBrowsingHistory(userId);
  const [isVisible, setIsVisible] = useState(false);
  const [hasShownOnce, setHasShownOnce] = useState(false);

  // Show the bar when user has viewed products
  useEffect(() => {
    if (hasHistory && browsingHistory.length >= 2 && !hasShownOnce) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setHasShownOnce(true);
      }, 2000); // Show after 2 seconds when user has viewed products

      return () => clearTimeout(timer);
    }
  }, [hasHistory, browsingHistory.length, hasShownOnce]);

  // Auto-hide after period of inactivity
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 30000); // Auto-hide after 30 seconds

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleClearAll = () => {
    clearHistory();
    setIsVisible(false);
  };

  // Don't render if loading or no history
  if (isLoading || !hasHistory) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border shadow-2xl"
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Recently Viewed</h3>
                <span className="text-sm text-muted-foreground">({browsingHistory.length} items)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsVisible(false)}
                  className="hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Swipe Instructions */}
            <div className="text-xs text-muted-foreground mb-3 text-center">
              Swipe left to remove • Swipe right to add to cart • Tap to view
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-6 scrollbar-hide">
              {browsingHistory.map((product, index) => (
                <SwipeableRecentProduct
                  key={`${product.id}-${index}`}
                  product={product}
                  onRemove={removeFromHistory}
                  index={index}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecentlyViewedBar;
